import { LocaleEnum } from "~/locales/resources";

export const API_ENDPOINT_NAMES = {
  quote: "quote",
  order: "order",
  fulfillmentEvent: "fulfillment_event",
} as const;

/**
 * API endpoints (relative paths)
 */
export const ENDPOINT = {
  // Auth endpoints
  auth: {
    login: "/auth/login",
    logout: "/auth/logout",
    refresh: "/auth/refresh",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    validateMagicToken: "/auth/validate-magic-token",
    validateMagicLink: "/auth/magic-link/validate",
    createMagicLink: "/auth/magic-link",
    impersonate: "/auth/impersonate",
  },

  // Purchase Order endpoints
  purchaseOrder: {
    list: "/purchase-order",
    byId: (id: string) => `/purchase-order/${id}`,
    byParams: (params: Record<string, string>) => `/purchase-order?${new URLSearchParams(params).toString()}`,
  },

  // Purchase Order Fulfillment Event endpoints
  purchaseOrderFulfillmentEvent: {
    list: "/purchase-order-fulfillment-event",
    byId: (id: string) => `/purchase-order-fulfillment-event/${id}`,
    update: (id: string) => `/purchase-order-fulfillment-event/${id}`,
  },

  // Leads endpoints
  leads: {
    create: "/leads/create",
  },

  // PQ Ingestion endpoints
  pqIngestion: {
    uploadAttachment: "/pq-ingestion/attachments/upload",
    createJob: "/pq-ingestion/jobs",
    getJobs: "/pq-ingestion/jobs",
    getJobById: "/pq-ingestion/jobs",
    getPQCandidates: (jobId: string, params?: { status?: string; page?: number; size?: number }) => {
      const baseUrl = `/pq-ingestion/jobs/${jobId}/pq-candidates`;
      if (params) {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.page !== undefined) queryParams.append("page", String(params.page));
        if (params.size !== undefined) queryParams.append("size", String(params.size));
        const queryString = queryParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      }
      return baseUrl;
    },
    submitPQCandidates: (jobId: string) => `/pq-ingestion/jobs/${jobId}/pq-candidates`,
    finalizeJob: (jobId: string) => `/pq-ingestion/jobs/${jobId}/finalize`,
  },

  // Products endpoints
  products: {
    search: "/products/search",
  },

  // Accounts/Suppliers endpoints
  accounts: {
    list: (params?: { name?: string; page?: number; size?: number }) => {
      const baseUrl = "/accounts";
      if (params) {
        const queryParams = new URLSearchParams();
        if (params.name) queryParams.append("name", params.name);
        if (params.page !== undefined) queryParams.append("page", String(params.page));
        if (params.size !== undefined) queryParams.append("size", String(params.size));
        const queryString = queryParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      }
      return baseUrl;
    },
  },

  // Incoterm location endpoints (TT-4549): GET takes no query params and returns the whole list (searched server-side
  // by src/utils/incotermLocationCatalog.ts); POST creates one, or returns the existing city + postal + country match.
  incotermLocations: {
    list: () => "/incoterm-locations",
    create: "/incoterm-locations",
  },

  // Money transfer / Payments endpoints
  moneyTransfer: {
    list: (params?: { page?: number; size?: number; fromDate?: string; toDate?: string }) => {
      const baseUrl = "/money-transfer";
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) queryParams.append("page", String(params.page));
      if (params?.size !== undefined) queryParams.append("size", String(params.size));
      if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params?.toDate) queryParams.append("toDate", params.toDate);
      const qs = queryParams.toString();
      return qs ? `${baseUrl}?${qs}` : baseUrl;
    },
    export: (params?: { fromDate?: string; toDate?: string }) => {
      const baseUrl = "/money-transfer/export";
      const queryParams = new URLSearchParams();
      if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params?.toDate) queryParams.append("toDate", params.toDate);
      const qs = queryParams.toString();
      return qs ? `${baseUrl}?${qs}` : baseUrl;
    },
  },

  // Handover endpoints
  handover: {
    list: (params?: { tab?: string; page?: number; size?: number }) => {
      const baseUrl = "/handover";
      if (params) {
        const queryParams = new URLSearchParams();
        if (params.tab) queryParams.append("tab", params.tab);
        if (params.page !== undefined) queryParams.append("page", String(params.page));
        if (params.size !== undefined) queryParams.append("size", String(params.size));
        const queryString = queryParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      }
      return baseUrl;
    },
    byId: (id: string) => `/handover/${id}`,
    uploadDocument: (id: string, documentType: string) =>
      `/handover/${id}/documents?document_type=${encodeURIComponent(documentType)}`,
    documentsBulkDownload: (id: string) => `/handover/${id}/documents/bulk-download`,
    deliveryNoteDownload: (id: string) => `/handover/${id}/delivery-note`,
  },

  // PQ (Product Quotations) endpoints
  pq: {
    byId: (id: string) => `/pq/${id}`,
    list: (params?: { page?: number; size?: number; status?: "ACTIVE" | "EXPIRING_SOON" | "EXPIRED" }) => {
      const baseUrl = "/pq";
      if (params) {
        const queryParams = new URLSearchParams();
        if (params.page !== undefined) queryParams.append("page", String(params.page));
        if (params.size !== undefined) queryParams.append("size", String(params.size));
        if (params.status) queryParams.append("status", params.status);
        const queryString = queryParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      }
      return baseUrl;
    },
    createSingleOffer: "/pq",
    update: "/pq",
    bulkUpdate: "/pq/bulk-update",
    updateSingleOffer: (id: string) => `/pq/${id}`,
  },
} as const;

export const AndercoreExternalLinks: Record<
  LocaleEnum,
  { privacy: string; imprint: string; terms: string; andercoreHome: string; faqs: string; linkedIn: string }
> = {
  de: {
    privacy: "https://andercore.com/de/datenschutz",
    imprint: "https://andercore.com/de/impressum/",
    terms: "https://www.andercore.com/de/agb-suppliers",
    andercoreHome: "https://andercore.com/de",
    faqs: "https://andercore.com/de/faqs/",
    linkedIn: "https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true",
  },
  en: {
    privacy: "https://andercore.com/privacy",
    imprint: "https://andercore.com/en/imprint/",
    terms: "https://www.andercore.com/terms-suppliers",
    andercoreHome: "https://andercore.com",
    faqs: "https://andercore.com/faqs/",
    linkedIn: "https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true",
  },
};
